import { useHoverPopoverClose } from '@app/components/base/HoverPopover';
import { MatchStatuses } from '@app/modules/audit/modules/station-audit/components/audit-result/MatchStatuses';
import { AuditMatch } from '@app/modules/audit/modules/station-audit/types';
import { CopyLinkAtTimestampButton } from '@app/modules/logger/screens/stream/components/popup/CopyLinkAtTimestampButton';
import { StreamPopup } from '@app/modules/logger/screens/stream/components/popup/StreamPopup';
import { StreamPopupHeader } from '@app/modules/logger/screens/stream/components/popup/StreamPopupHeader';
import { toTimestamp } from '@mi-front/core';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { Box, Typography } from '@mui/material';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { AuditMatchPlayPauseButton } from './AuditMatchPlayPauseButton';
import { useAuditMarkerCapture } from './useAuditMarkerCapture';

const SPOT_MATCH_MESSAGE = defineMessage({
  id: 'audit.matches.spotMatch',
  defaultMessage: 'Spot Match',
});

type AuditMatchInfoProps = {
  match: AuditMatch;
};

/**
 * Displays an audit match summary and its primary stream actions.
 */
export function AuditMatchInfo({ match }: AuditMatchInfoProps) {
  const { $t } = useIntl();
  const audio = match.audio!;
  const time = toTimestamp(match.datetime);
  const capture = useAuditMarkerCapture();
  const closePopover = useHoverPopoverClose();

  const handleCopyDone = useCallback(() => {
    capture({
      action: 'copy_link',
      kind: 'spot',
      marker_id: match.audio?.customId
        ? `${match.audio.customId}:${match.datetime}`
        : match.datetime,
    });
    closePopover?.();
  }, [capture, closePopover, match.audio?.customId, match.datetime]);

  return (
    <StreamPopup
      minWidth={280}
      maxWidth={360}
      header={
        <StreamPopupHeader
          icon={<AssignmentTurnedInOutlinedIcon />}
          title={$t(SPOT_MATCH_MESSAGE)}
          time={match.datetime}
        />
      }
      footer={
        <>
          <AuditMatchPlayPauseButton match={match} />
          <CopyLinkAtTimestampButton time={time} onDone={handleCopyDone} />
        </>
      }
    >
      <Typography
        variant="subtitle2"
        sx={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'break-word' }}
      >
        {audio.customId || audio.title}
      </Typography>

      <Box sx={{ mt: 1 }}>
        <MatchStatuses match={match} />
      </Box>
    </StreamPopup>
  );
}
